import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Alert, Avatar, Box, Button, Card, CardContent, CircularProgress, MenuItem, Stack, TextField, Typography } from "@mui/material";
import api, { post } from "../services/api";
import ENDPOINTS from "../utils/constants/endpoints";
import { DASHBOARD_COLORS as C } from "../components/DashboardCard";

// User Management is exposed to this role by the existing sidebar.
export const canManageUsers = (role) => role === "OQ==";
const emptyForm = { users_id: "", contact_person: "", designation: "", mobile: "", email: "", role_id: "", images: "" };
export default function AddUsers({ mode = "add", user = null, onSaved, onCancel, embedded = false, showPhoto = true }) {
  const role = useSelector((state) => state.auth.role_id);
  const navigate = useNavigate();
  const isEdit = mode === "edit";
  const [roles, setRoles] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(true);
  const [roleError, setRoleError] = useState("");
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [errors, setErrors] = useState({});
  const [form, setForm] = useState({ ...emptyForm, ...user, users_id: user?.users_mst_id || user?.users_id || "", contact_person: user?.name || user?.contact_person || "" });
  const allowed = canManageUsers(role);
  const fetchRoles = async () => {
    setRolesLoading(true); setRoleError("");
    try {
      const res = await post(ENDPOINTS.MASTER.ROLE_LIST, { token: localStorage.getItem("token") });
      if (res?.status && res.status !== "success") throw new Error();
      if (!Array.isArray(res?.data)) throw new Error();
      setRoles(res.data);
    } catch { setRoleError("Unable to load roles. Please retry."); }
    finally { setRolesLoading(false); }
  };
  useEffect(() => { if (allowed) fetchRoles(); }, [allowed]);
  const change = (e) => { setForm({ ...form, [e.target.name]: e.target.value }); setErrors({ ...errors, [e.target.name]: "" }); };
  const upload = async (file) => {
    if (!file || !allowed) return;
    setUploading(true); setFeedback(null);
    const data = new FormData(); data.append("token", localStorage.getItem("token")); data.append("image", file);
    try {
      const response = await api.post(ENDPOINTS.USERS.UPLOAD_PHOTO, data, { headers: { "Content-Type": "multipart/form-data" } });
      const res = response.data;
      if (res?.status !== "success") throw new Error();
      setForm((prev) => ({ ...prev, images: res.data?.image || res.data?.image_path || "" }));
    } catch { setFeedback({ severity: "error", text: "Image upload failed. Please try again." }); }
    finally { setUploading(false); }
  };
  const submit = async (event) => {
    event.preventDefault();
    if (!allowed || busy || uploading) return;
    const nextErrors = {};
    ["contact_person", "mobile", "role_id"].forEach((key) => { if (!String(form[key] ?? "").trim()) nextErrors[key] = "This field is required"; });
    setErrors(nextErrors); if (Object.keys(nextErrors).length) return;
    setBusy(true); setFeedback(null);
    try {
      const payload = Object.fromEntries(Object.keys(emptyForm).map((key) => [key, form[key] ?? ""]));
      const res = await post(isEdit ? ENDPOINTS.USERS.EDIT : ENDPOINTS.USERS.ADD, { token: localStorage.getItem("token"), ...payload });
      if (res?.status !== "success") { setFeedback({ severity: "error", text: res?.["success message"] || "Unable to save user. Please try again." }); return; }
      const text = `User ${isEdit ? "updated" : "added"} successfully`;
      setFeedback({ severity: "success", text });
      if (!isEdit) setForm({ ...emptyForm });
      onSaved?.(text);
    } catch { setFeedback({ severity: "error", text: "Unable to save user. Please try again." }); }
    finally { setBusy(false); }
  };
  if (!allowed) return <Alert severity="warning">You do not have access to User Management.</Alert>;
  return <Card elevation={0} sx={{ maxWidth: embedded ? "none" : 720, mx: "auto", border: embedded ? 0 : `1px solid ${C.border}`, borderRadius: "12px" }}>
    <CardContent sx={{ p: embedded ? 0 : { xs: 2, sm: 3 } }}>
      {!embedded && <Typography component="h1" variant="h5" fontWeight={700} color={C.navy} mb={3}>{isEdit ? "Edit User" : "Add User"}</Typography>}
      <Box component="form" onSubmit={submit} noValidate>
        <Stack spacing={2}>
          {feedback && <Alert severity={feedback.severity}>{feedback.text}</Alert>}
          {roleError && <Alert severity="error" action={<Button onClick={fetchRoles}>Retry</Button>}>{roleError}</Alert>}
          {showPhoto && <Stack direction="row" spacing={2} alignItems="center"><Avatar src={form.images || undefined} /><Button component="label" variant="outlined" disabled={busy || uploading}>{uploading ? "Uploading…" : "Upload Photo"}<input hidden type="file" accept="image/*" onChange={(e) => upload(e.target.files[0])} /></Button></Stack>}
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
            {[["contact_person", "Name"], ["designation", "Designation"], ["mobile", "Mobile"], ["email", "Email"]].map(([key, label]) => <TextField key={key} size="small" label={label} name={key} value={form[key] ?? ""} onChange={change} required={["contact_person", "mobile"].includes(key)} error={!!errors[key]} helperText={errors[key]} disabled={busy} fullWidth slotProps={{ htmlInput: { inputMode: key === "mobile" ? "tel" : key === "email" ? "email" : "text" } }} />)}
            <TextField size="small" select required label="Role" name="role_id" value={form.role_id ?? ""} onChange={change} disabled={busy || rolesLoading || !!roleError} error={!!errors.role_id} helperText={errors.role_id || (rolesLoading ? "Loading roles…" : " ")} fullWidth>
              {form.role_id && !roles.some((r) => String(r.role_id) === String(form.role_id)) && <MenuItem value={form.role_id} disabled>{user?.role || "Current role"}</MenuItem>}
              {roles.map((r) => <MenuItem key={r.role_id} value={r.role_id}>{r.role}</MenuItem>)}
            </TextField>
          </Box>
          <Stack direction="row" justifyContent="flex-end" spacing={1}>
            <Button disabled={busy || uploading} onClick={() => onCancel ? onCancel() : navigate("/users")} sx={{ textTransform: "none" }}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={busy || uploading || rolesLoading || !!roleError} startIcon={busy ? <CircularProgress size={16} color="inherit" /> : undefined} sx={{ bgcolor: C.primary, borderRadius: "9px", textTransform: "none", boxShadow: "none" }}>{busy ? "Saving…" : isEdit ? "Update User" : "Save User"}</Button>
          </Stack>
        </Stack>
      </Box>
    </CardContent>
  </Card>;
}
